import {
  BuyerSurveyResponse,
  PatchSurveyInterface,
} from '../../../../../shared/interfaces/survey.interface';
import { v4 as uuidv4 } from 'uuid';
import {
  CountryInterface,
  LanguageInterface,
} from '../../../../../shared/interfaces/create-surveys.interface';
import {
  McPreDraftSurvey,
  McSurveyRequest,
  MultiCountrySurvey,
  MultiCountrySurveyState,
} from '../interface/multi-country.interface';
import { MultiCoutrySurveyTitleExtractor } from './multi-country-survey-title-extractor';
import { MarginOverride } from '../../../../../shared/interfaces/survey.interface';
import { Template } from '../../../../layout/template-modal/template-modal.interface';
import { Constants as CreateSurveyConstants } from '../../../../../shared/constants/create-survey.constants';
import { DEFAULT } from '../../../../../shared/constants/defaults';

export class MultiCountryAddSurveyDataMapper {
  constructor(
    private _mcPreDraftSurveys: McPreDraftSurvey[],
    private readonly _isServiceBuyer: boolean
  ) {}

  public transform(): McSurveyRequest[] {
    const mc_unique_id = uuidv4();
    return this._mcPreDraftSurveys.map((mcPreDraftSurvey, index) =>
      this._mapper(mcPreDraftSurvey, mc_unique_id, index)
    );
  }

  private _mapper(
    mcPreDraftSurvey: McPreDraftSurvey,
    mc_unique_id: string,
    index: number
  ): McSurveyRequest {
    const {
      survey_localization,
      country_code,
      country_name,
      language_code,
      language_short_code,
      language_name,
      completes_required,
      click_balancing,
      survey_category_code,
      expected_loi,
      expected_ir,
      field_time,
      buyer_company,
      buyer_user_id,
      source,
      offer_price,
      margin_override,
      quotas = [],
      qualifications = [],
      aida,
    } = mcPreDraftSurvey;
    return {
      survey_title: this._title(mcPreDraftSurvey),
      survey_localization,
      country_code,
      country_name,
      language_code,
      language_short_code,
      language_name,
      survey_category_code,
      click_balancing,
      completes_required,
      expected_loi,
      expected_ir,
      field_time,
      buyer_company,
      buyer_user_id,
      source,
      mc_unique_id,
      mc_parent: index === 0,
      qualifications,
      quotas,
      aida,
      ...(this._isServiceBuyer && { offer_price, margin_override }),
    };
  }

  private _title({
    survey_title,
    country_code,
    language_code,
  }: McPreDraftSurvey): string {
    return `${survey_title} - MC-${country_code}-${language_code}`;
  }
}

export class MultiCountryFailedLocalesMapper {
  constructor(
    private readonly _failedRequests: { payload: McSurveyRequest }[]
  ) {}

  public transform(): string[] {
    return this._failedRequests.map((failedRequest) =>
      this._mapper(failedRequest)
    );
  }

  private _mapper({ payload }: { payload: McSurveyRequest }): string {
    const { country_name, language_name } = payload;
    return `${country_name}-${language_name}`;
  }
}

export class MultiCountrySurveyMapper {
  constructor(
    private readonly _surveys: BuyerSurveyResponse[],
    private readonly _mcUniqueId: string,
    private readonly _isServiceBuyer: boolean
  ) {}

  public transform(): MultiCountrySurvey[] {
    return this._surveys.map((survey) => this._mapper(survey));
  }

  private _mapper(survey: BuyerSurveyResponse): MultiCountrySurvey {
    const {
      ps_survey_id: survey_id,
      ps_survey_status,
      buyer_company,
      buyer_user_id,
      survey_title,
      survey_category_code,
      locale: {
        country_code,
        country_name,
        language_translate: language_code,
        language_code: language_short_code,
        language_name,
      },
      survey_localization,
      completes_required,
      click_balancing,
      expected_ir,
      expected_loi,
      field_time,
      currency_symbol,
      offer_price,
      qualifications = [],
      quotas = [],
      unique_links = { enable: false, items: [] },
      live_url,
      test_url,
      aida = DEFAULT.AIDA_PAYLOAD,
      mc,
    } = survey;
    return {
      buyer_company,
      buyer_user_id,
      survey_id,
      ps_survey_status,
      survey_title,
      survey_category_code,
      country_code,
      country_name,
      language_code,
      language_short_code,
      language_name,
      survey_localization,
      click_balancing,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      mc_unique_id: this._mcUniqueId,
      feasibility: 0,
      cpi: this._cpi(survey),
      price_override: this._priceOverride(survey),
      currency_symbol,
      loading: true,
      qualifications,
      quotas,
      aida,
      state: this._state(),
      ...(this._isServiceBuyer && {
        offer_price,
        margin_override: this._marginOverride(survey),
      }),
      unique_links,
      live_url,
      test_url,
      mc_parent: !!mc?.parent,
    };
  }

  private _cpi(survey: BuyerSurveyResponse): number {
    return survey.acpi ?? (survey.cpi.current || 0);
  }

  private _priceOverride(survey: BuyerSurveyResponse): number {
    return Number(survey.offer_price);
  }

  private _marginOverride(survey: BuyerSurveyResponse): MarginOverride {
    const margin = this._buyerMargin(survey);
    return {
      enable: !!margin,
      margin,
    };
  }

  private _buyerMargin(survey: BuyerSurveyResponse): number {
    return survey.margin_override && survey.margin_override.margin
      ? survey.margin_override.margin
      : 0;
  }

  private _state(): MultiCountrySurveyState {
    return {
      cpi: 'view',
    };
  }
}

export class MultiCountryNewSurveyRequestMapper {
  constructor(
    private readonly _survey: MultiCountrySurvey,
    private readonly _selectedCountry: CountryInterface,
    private readonly _selectedLanguage: LanguageInterface,
    private readonly _isServiceBuyer: boolean
  ) {}

  public transform(): McSurveyRequest {
    const {
      survey_category_code,
      click_balancing,
      completes_required,
      expected_loi,
      expected_ir,
      field_time,
      buyer_company,
      buyer_user_id,
      mc_unique_id,
      offer_price,
      margin_override,
      qualifications = [],
      quotas = [],
      unique_links,
      live_url,
      test_url,
    } = this._survey;
    const { short_Code: country_code, name: country_name } =
      this._selectedCountry;
    const {
      short_code: language_short_code,
      transalte_code: language_code,
      name: language_name,
    } = this._selectedLanguage;
    const aida = DEFAULT.AIDA_PAYLOAD;
    return {
      survey_title: this._title(),
      survey_localization: this._localization(),
      country_code,
      country_name,
      language_code,
      language_short_code,
      language_name,
      survey_category_code,
      click_balancing,
      completes_required,
      expected_loi,
      expected_ir,
      field_time,
      buyer_company,
      buyer_user_id,
      source: 'platform',
      mc_unique_id,
      mc_parent: false,
      qualifications,
      quotas,
      aida,
      ...(this._isServiceBuyer && { offer_price, margin_override }),
      unique_links,
      ...(live_url ? { live_url } : {}),
      ...(test_url ? { test_url } : {}),
    };
  }

  private _title(): string {
    const { short_Code: country_code } = this._selectedCountry;
    const { transalte_code: language_code } = this._selectedLanguage;
    const baseTitle = new MultiCoutrySurveyTitleExtractor(
      this._survey
    ).extract();
    return `${baseTitle} - MC-${country_code}-${language_code}`;
  }

  private _localization(): string {
    const { short_Code: country_code } = this._selectedCountry;
    const { transalte_code: language_code } = this._selectedLanguage;
    return `${language_code}_${country_code}`;
  }
}

export class MultiCountrySameCountryExclusionPayloadMapper<
  T extends { ps_survey_id: number; survey_localization: string }
> {
  constructor(
    private readonly _surveys: T[],
    private readonly _currentSurvey: T
  ) {}

  public transform(): PatchSurveyInterface[] {
    return [
      {
        op: 'replace',
        path: '/survey_grouping',
        value: this._exclusions(),
      },
    ];
  }

  private _exclusions(): number[] {
    return this._surveys
      .filter(
        (s) =>
          s.ps_survey_id !== Number(this._currentSurvey.ps_survey_id) &&
          this._countryCode(s) === this._countryCode(this._currentSurvey)
      )
      .map((s) => s.ps_survey_id)
      .map(Number);
  }

  private _countryCode(survey: T): string {
    return survey.survey_localization.split('_')[1];
  }
}

export class MultiCountryTemplateBasedSurveyRequestMapper {
  constructor(
    private readonly _existingSurveys: MultiCountrySurvey[],
    private readonly _currentSurveyId: number,
    private readonly _template: Template,
    private readonly _countries: CountryInterface[],
    private readonly _isServiceBuyer: boolean
  ) {}

  public transform(): McSurveyRequest {
    const {
      id: template_id,
      survey_localization,
      survey_category_code,
      click_balancing,
      completes_required,
      expected_loi,
      expected_ir,
      field_time,
      qualifications = [],
      quotas = [],
    } = this._template;
    const aida = DEFAULT.AIDA_PAYLOAD;
    return {
      survey_title: this._title(),
      survey_localization,
      ...this._localeFields(),
      survey_category_code,
      click_balancing,
      completes_required,
      expected_loi,
      expected_ir,
      field_time,
      ...this._buyerFields(),
      source: 'platform',
      mc_unique_id: this._mcUniqueId(),
      mc_parent: false,
      template_id,
      qualifications,
      quotas,
      aida,
      ...(this._isServiceBuyer && this._waspSurveyFields()),
    };
  }

  private _title(): string {
    const [language_code, country_code] =
      this._template.survey_localization.split('_');
    const baseTitle = new MultiCoutrySurveyTitleExtractor(
      this._existingSurveys[0]
    ).extract();
    return `${baseTitle} - MC-${country_code}-${language_code}`;
  }

  private _localeFields() {
    const [languageCode, countryCode] =
      this._template.survey_localization.split('_');
    const {
      lang: languages = [],
      short_Code: country_code,
      name: country_name,
    } = this._countries.find((c) => c.short_Code === countryCode)!;
    const {
      transalte_code: language_code,
      name: language_name,
      short_code: language_short_code,
    } = languages.find((l) => l.transalte_code === languageCode)!;
    return {
      country_code,
      country_name,
      language_code,
      language_short_code,
      language_name,
    };
  }

  private _buyerFields(): { buyer_company: number; buyer_user_id: number } {
    const { buyer_company, buyer_user_id } = this._existingSurveys[0];
    return {
      buyer_company,
      buyer_user_id,
    };
  }

  private _mcUniqueId(): string {
    return this._existingSurveys[0].mc_unique_id || '';
  }

  private _waspSurveyFields(): {
    offer_price?: number;
    margin_override?: MarginOverride;
  } {
    if (!this._exists()) {
      const { offer_price, margin_override } = this._currentSurvey();
      return {
        offer_price,
        margin_override,
      };
    }

    const {
      offer_price,
      margin = CreateSurveyConstants.AVERAGE_VARIABLE_MARGIN,
    } = this._template;
    return {
      offer_price,
      margin_override: {
        enable: !!offer_price,
        margin,
      },
    };
  }

  private _exists(): boolean {
    return this._existingSurveys.some(
      (s) => s.survey_localization === this._template.survey_localization
    );
  }

  private _currentSurvey(): MultiCountrySurvey {
    return this._existingSurveys.find(
      (s) => s.survey_id === Number(this._currentSurveyId)
    )!;
  }
}
